import { styled, SxProps, Theme } from '@mui/material';
import {
  TOOLBAR_BUTTON_HEIGHT,
  TOOLBAR_BUTTON_PADDING_PX,
  TOOLBAR_ICON_FONT_SIZE,
} from '../styles';
import { HexColorInput } from 'react-colorful';

/** The rounded ends of the pill need a little more room than the round icon buttons. */
const PILL_END_PADDING_PX = TOOLBAR_BUTTON_PADDING_PX + 1;
/** Between the "A" and the arrow: they read as one control. */
const PILL_INNER_PADDING_PX = 2;

/** Above dialogs and the sticky headers of the forms the editor is used in. */
export const pickerPopperSx: SxProps<Theme> = {
  zIndex: (theme) => theme.zIndex.modal + 1,
};

const SWATCH_SIZE_PX = 20;
/** Ten basic colours and the spectrum: two rows. */
const SWATCHES_PER_ROW = 6;
const MENU_WIDTH_PX = 200;

export const pickerPaperSx: SxProps<Theme> = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  padding: 1.5,
  width: MENU_WIDTH_PX,
  '& .react-colorful': {
    width: '100%',
    height: 140,
  },
};

/** "A" and its arrow read as one rounded control, as in word processors: one hover shape, not two circles. */
export const splitButtonSx = (disabled?: boolean): SxProps<Theme> => ({
  display: 'inline-flex',
  alignItems: 'stretch',
  // The height of the other toolbar buttons: the icons stay on one line.
  height: TOOLBAR_BUTTON_HEIGHT,
  // Fully rounded, like the round hover of the other toolbar buttons.
  borderRadius: TOOLBAR_BUTTON_HEIGHT,
  overflow: 'hidden',
  opacity: disabled ? 0.38 : 1,
  '& > span': {
    display: 'flex',
  },
  '&:hover': {
    backgroundColor: disabled ? 'transparent' : 'action.hover',
  },
});

const splitButtonPartSx = {
  height: '100%',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'action.focus',
  },
} as const;

export const splitButtonApplySx: SxProps<Theme> = {
  ...splitButtonPartSx,
  paddingLeft: `${PILL_END_PADDING_PX}px`,
  paddingRight: `${PILL_INNER_PADDING_PX}px`,
};

export const splitButtonMenuSx = (isOpen: boolean): SxProps<Theme> => ({
  ...splitButtonPartSx,
  paddingLeft: 0,
  paddingRight: `${PILL_END_PADDING_PX - 1}px`,
  ...(isOpen ? { backgroundColor: 'action.selected' } : {}),
});

/** The "A" in a ring, both in the colour the button applies. */
export const colorLetterSx = (color: string): SxProps<Theme> => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  // The size of the other toolbar icons.
  width: TOOLBAR_ICON_FONT_SIZE,
  height: TOOLBAR_ICON_FONT_SIZE,
  borderRadius: '50%',
  border: '1.5px solid',
  borderColor: color,
  color,
  fontSize: '0.7rem',
  fontWeight: 600,
  lineHeight: 1,
});

export const menuArrowIconSx: SxProps<Theme> = {
  fontSize: '1rem',
  color: 'text.secondary',
};

export const menuSectionTitleSx: SxProps<Theme> = {
  color: 'text.secondary',
};

export const swatchGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: `repeat(${SWATCHES_PER_ROW}, ${SWATCH_SIZE_PX}px)`,
  justifyContent: 'space-between',
  rowGap: 1,
};

const swatchShapeSx = {
  width: SWATCH_SIZE_PX,
  height: SWATCH_SIZE_PX,
  borderRadius: '4px',
  border: 1,
  borderColor: 'divider',
  '&:hover': {
    transform: 'scale(1.15)',
  },
  '&.Mui-focusVisible': {
    outline: 2,
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
} as const;

/** A light colour still shows its edge on the white menu. */
export const swatchSx = (color: string): SxProps<Theme> => ({
  ...swatchShapeSx,
  backgroundColor: color,
});

/** Every hue: opens the picker for a colour that is not in the lists. */
export const spectrumSwatchSx: SxProps<Theme> = {
  ...swatchShapeSx,
  background:
    'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
};

export const swatchCheckSx: SxProps<Theme> = {
  fontSize: '0.9rem',
  color: 'common.white',
};

export const customPickerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

/** Typing a hex value, for a colour used elsewhere (a brand colour). */
export const HexInput = styled(HexColorInput)(({ theme }) => ({
  boxSizing: 'border-box',
  width: '100%',
  padding: theme.spacing(0.75, 1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  textTransform: 'uppercase',
  '&:focus': {
    outlineColor: theme.palette.primary.main,
  },
}));
